import type { EventLogV2Payload } from 'src/types/requests/eventslog/events_log';

type ComponentSnapshots = Record<string, Record<string, unknown>>;
type PayloadRecord = Record<string, unknown>;

/** Prefer the component that usually carries the event’s primary state change. */
const PRIMARY_COMPONENT_PREFIXES = [
	'switch',
	'light',
	'rgbw',
	'rgb',
	'cct',
	'cover',
	'input',
	'cb',
	'motion',
	'smoke',
	'temperature',
	'humidity',
	'illuminance',
	'devicepower',
];

const resolveComponentKey = (
	cs: ComponentSnapshots | undefined,
	ps: ComponentSnapshots | undefined,
): string | undefined => {
	const csKeys = cs ? Object.keys(cs) : [];
	const psKeys = ps ? Object.keys(ps) : [];
	const sharedKeys =
		cs && ps ? csKeys.filter((key) => key in ps) : csKeys.length > 0 ? csKeys : psKeys;

	for (const prefix of PRIMARY_COMPONENT_PREFIXES) {
		const match = sharedKeys.find((key) => key === prefix || key.startsWith(`${prefix}:`));
		if (match) {
			return match;
		}
	}

	return sharedKeys[0];
};

/**
 * Flattens nested status shapes (e.g. battery.percent) so EVENT_V2_NORMALIZERS
 * can read them with existing field aliases.
 */
const flattenComponentFields = (fields: Record<string, unknown>): PayloadRecord => {
	const flattened: PayloadRecord = { ...fields };

	const battery = fields.battery;
	if (battery && typeof battery === 'object' && !Array.isArray(battery)) {
		const percent = (battery as PayloadRecord).percent ?? (battery as PayloadRecord).value;
		if (typeof percent === 'number') {
			flattened.battery = percent;
			flattened.bat = percent;
		}
	}

	return flattened;
};

/**
 * Merges every component snapshot into one record so multi-component events
 * (e.g. motion + devicepower + illuminance) keep battery/lux/tmp in the UI.
 */
const mergeComponentSnapshots = (
	snapshots: ComponentSnapshots | undefined,
): PayloadRecord | undefined => {
	if (!snapshots) {
		return undefined;
	}

	const merged: PayloadRecord = {};
	for (const fields of Object.values(snapshots)) {
		if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
			continue;
		}
		Object.assign(merged, flattenComponentFields(fields));
	}

	return Object.keys(merged).length > 0 ? merged : undefined;
};

/**
 * Maps BE v2 snapshot payloads ({ cs, ps }) into the canonical shape
 * ({ component, current, previous }) expected by EVENT_V2_NORMALIZERS.
 * Flat keyed UEV objects are handled separately by normalizeFlatEventLogPayload.
 */
export const coerceEventLogV2Payload = (payload: EventLogV2Payload): EventLogV2Payload => {
	if (payload.current || payload.previous) {
		return payload;
	}

	const cs = payload.cs;
	const ps = payload.ps;
	if (!cs && !ps) {
		return payload;
	}

	const componentKey = resolveComponentKey(cs, ps);
	const current = mergeComponentSnapshots(cs);
	const previous = mergeComponentSnapshots(ps);

	if (!current && !previous) {
		return payload;
	}

	return {
		...payload,
		component: payload.component ?? componentKey,
		current,
		previous,
	};
};
